import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import TrackingMap from "@/components/TrackingMap";
import StatusBadge from "@/components/StatusBadge";
import { STATUS_CONFIG, fmtTime, fmtDateTime, fromNow, isStalePing, fmtMiles } from "@/lib/constants";
import { MapPin, Truck, User, Clock, Navigation, Loader2, AlertTriangle } from "lucide-react";

export default function TrackingPage() {
  const { token } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/track/${token}`);
      setT(data);
      setErr(false);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-stark-brand">
        <img src="/stark-logo-white.png" alt="STARK" className="h-9 w-auto" />
        <Loader2 className="h-6 w-6 animate-spin text-stark-terracotta" />
      </div>
    );
  }

  if (err || !t) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-stark-brand px-6 text-center">
        <img src="/stark-logo-white.png" alt="STARK" className="mb-8 h-9 w-auto" />
        <AlertTriangle className="h-10 w-10 text-stark-terracotta" />
        <h1 className="mt-4 text-2xl font-extrabold text-white">Tracking link invalid</h1>
        <p className="mt-1 text-white/50">This link may have expired or the job has been completed.</p>
      </div>
    );
  }

  const eta = t.eta_minutes != null ? Math.max(0, Math.round(t.eta_minutes)) : null;
  const hasLive = t.current_lat != null && !isStalePing(t.last_ping_at);

  return (
    <div className="min-h-screen bg-stark-brand">
      {/* Brand accent bar */}
      <div className="h-1.5 w-full bg-stark-terracotta" />
      {/* Glass header with STARK logo */}
      <header className="stark-glass-dark sticky top-0 z-20 flex items-center gap-3 px-5 py-3.5">
        <img src="/stark-logo-white.png" alt="STARK" className="h-6 w-auto" />
        <div className="h-6 w-px bg-white/15" />
        <div className="text-[11px] font-medium text-white/50">Live delivery tracking</div>
        <div className="ml-auto"><StatusBadge status={t.status} size="sm" /></div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6">
        {/* ETA hero */}
        <div className="rounded-2xl border-t-4 border-stark-terracotta bg-white p-6 text-center shadow-xl">
          {t.status === "en_route" ? (
            hasLive ? (
              <>
                <div className="text-sm font-semibold uppercase tracking-wide text-stark-terracotta">Arriving in</div>
                <div className="mt-1 text-5xl font-extrabold tracking-tight text-stark-ink">
                  {eta != null ? `${eta}` : "—"} <span className="text-2xl font-bold text-stark-muted">min</span>
                </div>
                {t.distance_km != null && <div className="mt-1 text-sm text-stark-muted">{fmtMiles(t.distance_km)} away · updated {fromNow(t.last_ping_at) || "just now"}</div>}
              </>
            ) : (
              <>
                <div className="relative mx-auto flex h-14 w-14 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-stark-terracotta/30" />
                  <Navigation className="relative h-8 w-8 text-stark-terracotta" />
                </div>
                <div className="mt-3 text-2xl font-extrabold text-stark-ink">Your STARK team is on the way</div>
                <p className="mt-1 text-sm text-stark-muted">Locating your crew… their live position will appear here in a moment.</p>
                {t.scheduled_at && (
                  <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full bg-stark-tint px-4 py-1.5 text-sm font-semibold text-stark-ink">
                    <Clock className="h-4 w-4 text-stark-terracotta" />
                    Scheduled {fmtDateTime(t.scheduled_at)}{t.window_minutes ? ` (±${t.window_minutes} min)` : ""}
                  </div>
                )}
                <div className="mt-3 flex items-start justify-center gap-1.5 text-sm text-stark-muted">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {t.address}
                </div>
              </>
            )
          ) : t.status === "arrived" ? (
            <>
              <Navigation className="mx-auto h-8 w-8 text-stark-success" />
              <div className="mt-2 text-2xl font-extrabold text-stark-ink">Your STARK team has arrived</div>
            </>
          ) : t.status === "completed" ? (
            <div className="text-2xl font-extrabold text-stark-ink">Delivery completed 🎉</div>
          ) : (
            <>
              <div className="text-sm font-semibold uppercase tracking-wide text-stark-muted">Status</div>
              <div className="mt-1 text-2xl font-extrabold text-stark-ink">{STATUS_CONFIG[t.status]?.label}</div>
              <p className="mt-1 text-sm text-stark-muted">We'll update this page the moment your team departs.</p>
            </>
          )}
        </div>

        {/* Map */}
        <div className="mt-4 overflow-hidden rounded-2xl shadow-xl">
          <TrackingMap
            dest={{ lat: t.dest_lat, lng: t.dest_lng }}
            current={t.current_lat != null ? { lat: t.current_lat, lng: t.current_lng } : null}
            driverName={t.driver_name}
            customerName={t.customer_name}
            path={t.route_geometry}
            height={340}
          />
        </div>

        {/* Details */}
        <div className="mt-4 space-y-3 rounded-2xl bg-white p-5 shadow-xl">
          <div className="flex items-center gap-3 border-b border-stark-border pb-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stark-moss text-white">
              <User className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-muted">Your crew</div>
              <div className="font-bold text-stark-ink">{t.driver_name || "STARK team"}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-4 w-4 text-stark-muted" />
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-muted">Destination</div>
              <div className="text-sm font-medium text-stark-ink">{t.address}</div>
            </div>
          </div>
          {t.truck_name && (
            <div className="flex items-start gap-3">
              <Truck className="mt-0.5 h-4 w-4 text-stark-muted" />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-muted">Vehicle</div>
                <div className="text-sm font-medium text-stark-ink">{t.truck_name}</div>
              </div>
            </div>
          )}
          {t.departed_at && (
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 text-stark-muted" />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-stark-muted">Departed</div>
                <div className="text-sm font-medium text-stark-ink">{fmtTime(t.departed_at)}</div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <img src="/stark-logo-white.png" alt="STARK" className="h-5 w-auto opacity-50" />
          <p className="text-center text-xs text-white/30">Powered by <span className="font-semibold text-white/50">STARK Fleet</span> · This page refreshes automatically.</p>
        </div>
      </div>
    </div>
  );
}
